import {
  Timeline as TimelineIcon,
  Refresh,
  ArrowUpward,
  Info,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Fab,
  Stack,
  Typography,
  Zoom,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useGetCustomerTimelineQuery } from "../../store/services/timelineApiRTK";
import { Work, WorkItemList } from "../../store/types/work.types";
import {
  processTimelineData,
  enrichTimelineItems,
  filterTimelineItems,
} from "../../utils/timelineProcessor";
import {
  TimelineFilters,
  defaultTimelineFilters,
} from "../../types/Timeline.types";
import TimelineItem from "./TimelineItem";
import TimelineFiltersComponent from "./TimelineFilters";
import { ProcessedTimelineItem } from "../../types/Timeline.types";

interface CustomerTimelineProps {
  customerNo: number;
  onWorkClick?: (workId: number) => void;
  onWorkItemClick?: (workItemId: number) => void;
}

const CustomerTimeline = ({
  customerNo,
  onWorkClick,
  onWorkItemClick,
}: CustomerTimelineProps) => {
  const [filters, setFilters] = useState<TimelineFilters>(defaultTimelineFilters);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const {
    data: timeline,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCustomerTimelineQuery(customerNo, {
    refetchOnMountOrArgChange: true,
  });

  // Process timeline data
  const processedItems = useMemo(() => {
    if (!timeline) return [];
    
    const rawItems = processTimelineData(timeline);
    const enrichedItems = enrichTimelineItems(rawItems);
    return filterTimelineItems(enrichedItems, filters);
  }, [timeline, filters]);

  // Calculate item counts for filters
  const itemCounts = useMemo(() => {
    if (!timeline) return { eventData: 0, works: 0, eventLogs: 0, workItems: 0 };
    
    return {
      eventData: timeline.Datas.length,
      works: timeline.Works.length,
      eventLogs: timeline.EventLogs.length,
      workItems: timeline.WorkItems.length,
    };
  }, [timeline]);

  // Handle scroll to top
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle item clicks
  const handleItemClick = (item: ProcessedTimelineItem) => {
    switch (item.type) {
      case 'work':
        if (onWorkClick) {
          const work = item.data as Work;
          onWorkClick(work.Id);
        }
        break;
      case 'workitem':
        if (onWorkItemClick) {
          const workItem = item.data as WorkItemList;
          onWorkItemClick(workItem.Id);
        }
        break;
      default:
        break;
    }
  };

  const isRefreshing = isFetching && !isLoading;

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={48} />
          <Typography variant="body2" color="text.secondary">
            Laddar tidslinje...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        <Typography variant="body1">
          Kunde inte ladda tidslinjen. Försök igen senare.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() => refetch()}
          sx={{ mt: 1 }}
        >
          Försök igen
        </Button>
      </Alert>
    );
  }

  if (!timeline) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography variant="body1">
          Ingen tidslinje-data tillgänglig för denna kund.
        </Typography>
      </Alert>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 2, position: "relative" }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={2}>
              <TimelineIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h5" fontWeight="600">
                  Kundtidslinje
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {processedItems.length} händelser visas
                </Typography>
              </Box>
              {isRefreshing && (
                <CircularProgress size={20} />
              )}
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              disabled={isRefreshing}
              sx={{ borderRadius: 2 }}
            >
              Uppdatera
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Filters */}
      <TimelineFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        itemCounts={itemCounts}
      />

      {/* Timeline Items */}
      <Box sx={{ position: "relative", pl: 2 }}>
        {/* Timeline line */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: "primary.light",
            opacity: 0.3,
          }}
        />

        {processedItems.length > 0 ? (
          <Stack spacing={0}>
            {processedItems.map((item) => (
              <TimelineItem
                key={item.id}
                item={item}
                onItemClick={handleItemClick}
              />
            ))}
          </Stack>
        ) : (
          <Card>
            <CardContent sx={{ textAlign: "center", py: 6 }}>
              <Info color="action" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Inga händelser att visa
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prova att justera filtren eller kontrollera om det finns data
                för denna kund.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Scroll to top button */}
      <Zoom in={showScrollTop}>
        <Fab
          color="primary"
          size="small"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
        >
          <ArrowUpward />
        </Fab>
      </Zoom>
    </Container>
  );
};

export default CustomerTimeline;